import { logError } from "./debug-print";
import { getFeatureFlag } from "./feature-flags";
import { getFeatureSettingProbability } from "./feature-settings";
import { setCloudRegion } from "./function-region";
import type { LocationSettings } from "./location-common";
import { passFunctionResponseToNativeCode } from "./native-hooks";
import { getStaticAppConfig } from "./static-app";
import { exceptionToString, maybe } from "./Support";
import { frontendTrace } from "./tracing";

type Superfunction =
    | "builderFunctionSmall"
    | "builderFunctionLarge"
    | "playerFunctionSmall"
    | "playerFunctionLarge"
    | "playerFunctionCritical";

const functionDispatchTable: Record<string, Superfunction> = {
    acquireStripeSession: "builderFunctionSmall",
    acquireStripeSessionForProPurchase: "builderFunctionSmall",
    acquireStripeSessionForTemplatePurchase: "builderFunctionSmall",
    addTableToApp: "builderFunctionLarge",
    linkTablesToApp: "builderFunctionSmall",
    removeTableFromApp: "builderFunctionSmall",
    duplicateTableInApp: "builderFunctionSmall",
    syncTables: "builderFunctionSmall",
    deleteTable: "builderFunctionSmall",
    listTables: "builderFunctionSmall",
    setBoostsForOwner: "builderFunctionSmall",
    updateUnifiedPaymentInformation: "builderFunctionSmall",
    broadcastNotificationToAppUsers: "builderFunctionSmall",
    listStripeSubscriptions: "builderFunctionSmall",
    checkDomainConfigured: "builderFunctionSmall",
    createOrganization: "builderFunctionSmall",
    deleteOrganization: "builderFunctionSmall",
    setOrgAdmin: "builderFunctionSmall",
    acceptOrganizationInvite: "builderFunctionSmall",
    inviteUserToTransferApp: "builderFunctionSmall",
    generateAppFromDescription: "builderFunctionLarge",
    getOAuth2TokensForGoogleSheets: "builderFunctionSmall",
    getOrganizationBilling: "builderFunctionSmall",
    getOrganizationMembers: "builderFunctionSmall",
    getOrganizationPersistentInviteLink: "builderFunctionSmall",
    getOrganizationPlans: "builderFunctionSmall",
    generatePublishedAppDataFromData: "builderFunctionSmall",
    getAIPlaygroundImages: "builderFunctionSmall",
    generateAICompletion: "builderFunctionSmall",
    getUnsplashImages: "builderFunctionSmall",
    integrateWithStripe: "builderFunctionSmall",
    integrateWithMicrosoft: "builderFunctionSmall",
    integrateWithAirtable: "builderFunctionSmall",
    convertAuthToCustomToken: "builderFunctionSmall",
    inviteToOrganization: "builderFunctionSmall",
    modifySyntheticColumns: "builderFunctionLarge",
    moveTableInSchema: "builderFunctionSmall",
    moveOrgInUser: "builderFunctionSmall",
    inviteAppUser: "builderFunctionSmall",
    pingUnsplashDownload: "builderFunctionSmall",
    removeFromOrganization: "builderFunctionSmall",
    setAppPlan: "builderFunctionSmall",
    setAppPlanUnified: "builderFunctionSmall",
    setCustomDomain: "builderFunctionSmall",
    setEmailOwnersColumns: "builderFunctionSmall",
    setOrUpdateUser: "builderFunctionSmall",
    setOrgOnboarding: "builderFunctionSmall",
    createOrUpdateHubspotContact: "builderFunctionSmall",
    isEmailSignedUp: "builderFunctionSmall",
    setShortName: "builderFunctionSmall",
    setPublishingActive: "builderFunctionSmall",
    discardPausedAppChanges: "builderFunctionSmall",
    transferAppBetweenUsers: "builderFunctionSmall",
    transferAppToOrganization: "builderFunctionSmall",
    acceptAppInvite: "builderFunctionLarge",
    uploadImg: "builderFunctionSmall",
    createAppFromTemplate: "builderFunctionLarge",
    deleteApp: "builderFunctionLarge",
    unpublishApp: "builderFunctionLarge",
    duplicateApp: "builderFunctionLarge",
    submitTemplate: "builderFunctionLarge",
    generatePublishedAppDataFromSheet: "builderFunctionLarge",
    reloadPublishedAppDataFromSheet: "builderFunctionLarge",
    prepareReplaceGoogleSheetInApp: "builderFunctionLarge",
    exportAppData: "builderFunctionLarge",
    deleteAppUserForApp: "builderFunctionLarge",
    newGlideTableApp: "builderFunctionLarge",
    newApp: "builderFunctionLarge",
    authorizeUserForApp: "playerFunctionCritical",
    geocodeAddresses: "playerFunctionSmall",
    getAppUserForAuthenticatedUser: "playerFunctionSmall",
    getCustomTokenForApp: "playerFunctionCritical",
    getPasswordForEmailPin: "playerFunctionCritical",
    getPasswordForOAuth2Token: "playerFunctionCritical",
    inAppPurchase: "playerFunctionSmall",
    registerForPushNotifications: "playerFunctionSmall",
    reportGeocodesInApp: "playerFunctionSmall",
    sendPinForEmail: "playerFunctionSmall",
    stripeInAppPurchaseConfirmIntent: "playerFunctionSmall",
    uploadAppFile: "playerFunctionSmall",
    uploadAppImg: "playerFunctionSmall",
    uploadAppFileV2: "playerFunctionLarge",
    getAppSnapshot: "playerFunctionCritical",
    sendShareSMS: "builderFunctionSmall",
    ensureDataLiveliness: "playerFunctionSmall",
    triggerZap: "playerFunctionSmall",
    deliverEmailFromAction: "playerFunctionSmall",
    testIframeEmbeddable: "playerFunctionSmall",
    getPreviewAsUser: "builderFunctionSmall",
    sendAppFeedback: "playerFunctionSmall",
    reportApp: "playerFunctionSmall",
    appBeacon: "playerFunctionSmall",
    triggerAppWebhookAction: "playerFunctionSmall",
    callAPIColumn: "playerFunctionSmall",
    renameTable: "builderFunctionSmall",
    renameOrganization: "builderFunctionSmall",
    previewCharges: "builderFunctionSmall",
    applyPromoCode: "builderFunctionSmall",
    legacyUpgrade: "builderFunctionSmall",
    makeSupportCodeForApp: "builderFunctionSmall",
    accessSupportCode: "builderFunctionSmall",
    setAdditionalBillingInfo: "builderFunctionSmall",
    getTaxInfo: "builderFunctionSmall",
    setTaxInfo: "builderFunctionSmall",
    deleteTaxInfo: "builderFunctionSmall",
    requestDownloadLinkForExport: "builderFunctionSmall",
    addToMediaLibrary: "builderFunctionSmall",
    requestDataExport: "builderFunctionSmall",
    addWebhook: "builderFunctionSmall",
    createAndSendAuthLink: "builderFunctionSmall",
    sendEmailVerification: "builderFunctionSmall",
    storeChurnSurvey: "builderFunctionSmall",
    finishOnboarding: "builderFunctionSmall",
    reportQuotaIncreases: "playerFunctionSmall",
    setAppIsFavorite: "builderFunctionSmall",
    callOpenWeatherMapApi: "builderFunctionSmall",
    getAirtableBases: "builderFunctionSmall",
    getExcelWorkbooks: "builderFunctionSmall",
    getGlideSubscription: "builderFunctionSmall",
    subscribeToAddons: "builderFunctionLarge",
    cancelAppPlan: "builderFunctionLarge",
    getOwnerEminence: "builderFunctionSmall",
    getAppEminence: "playerFunctionCritical",
    previewAddonCharges: "builderFunctionSmall",
    deleteAccount: "builderFunctionLarge",
    sendHubspotCustomEvent: "builderFunctionSmall",
    loadBuilderActions: "builderFunctionSmall",
    getLatestPriceForAddon: "builderFunctionSmall",
    getLoginLogs: "builderFunctionSmall",
    getAppModifications: "builderFunctionSmall",
    getOrgEditorCount: "builderFunctionSmall",
    getAppDataForOrg: "builderFunctionSmall",
    getQuotasForApps: "builderFunctionSmall",
};

const ignoreInStaticMode = new Set(["ensureDataLiveliness"]);

for (const [fn, superFn] of Object.entries(functionDispatchTable)) {
    if (superFn.startsWith("builder") || superFn === "playerFunctionCritical") {
        ignoreInStaticMode.add(fn);
    }
}

enum OriginKind {
    Same = "same",
    Cross = "cross",
    Container = "container",
    Api = "api",
}

// We need to hook all HTTP calls to Cloud Functions to determine
// whether or not we're suffering from network connectivity issues.
// Unfortunately, we react to network connectivity issues in
// app/src/webapp, upon which we cannot depend in app/src/common.
// So instead, someone has to register the reaction from the outside world.
type NetworkErrorStateHandler = (locationSettings: LocationSettings, functionName: string) => void;
const onNetworkError = new Set<NetworkErrorStateHandler>();
const onNetworkSuccess = new Set<NetworkErrorStateHandler>();

export function addOnFunctionNetworkError(handler: NetworkErrorStateHandler): void {
    onNetworkError.add(handler);
}

export function removeOnFunctionNetworkError(handler: NetworkErrorStateHandler): void {
    onNetworkError.delete(handler);
}

export function addOnFunctionNetworkSuccess(handler: NetworkErrorStateHandler): void {
    onNetworkSuccess.add(handler);
}

export function removeOnFunctionNetworkSuccess(handler: NetworkErrorStateHandler): void {
    onNetworkSuccess.delete(handler);
}

function runHandlers(
    locationSettings: LocationSettings,
    functionName: string,
    handlers: Iterable<NetworkErrorStateHandler>
): void {
    for (const handler of handlers) {
        maybe(() => handler(locationSettings, functionName), undefined);
    }
}

const defaultCallHeaderTimeout = 125000;
const builderCallHeaderTimeout = 300000;

function singleFetchNoRetry(
    functionName: string,
    fullFunctionName: string,
    url: string,
    headers: Record<string, string>,
    body: any,
    originKind: OriginKind,
    superFunction: string | undefined,
    timeout: number
): Promise<Response | undefined> {
    return frontendTrace(
        "function",
        { function: functionName, url, origin: originKind, superfunction: superFunction },
        async fields => {
            // FIXME: We need a timeout for the body too!
            // This needs to be provided by callers, as well.
            const abortController = new AbortController();
            const timeoutHandle = setTimeout(() => abortController.abort(), timeout);

            try {
                const response = await fetch(url, {
                    method: "POST",
                    body,
                    headers: {
                        "Content-Type": "application/json",
                        ...headers,
                    },
                    signal: abortController.signal,
                });
                clearTimeout(timeoutHandle);

                fields.status = response.status;
                fields.success = response.ok;

                // This code path is fine for all unauthenticated Cloud Functions.
                // Unauthenticated functions return signed URLs to actually-public
                // resources, so we can skip the entire call the second time around.
                //
                // However, we _must_ only fall back to this response if the network
                // is unavailable.
                if (["getAppSnapshot"].includes(functionName)) {
                    await passFunctionResponseToNativeCode(fullFunctionName, url, response.clone());
                }

                return response;
            } catch (e: unknown) {
                // FIXME: Appropriately handle exceptions here
                if (!(e instanceof DOMException) || e.code !== e.ABORT_ERR) {
                    logError("In MaterialAppFacilities.callCloudFunction:", e);

                    fields.exception = exceptionToString(e);
                }
                fields.success = false;

                return undefined;
            }
        }
    );
}

function isContainerEnabled(
    flagOrSetting:
        | "usePlayerCriticalContainer"
        | "usePlayerSmallContainer"
        | "usePlayerLargeContainer"
        | "useBuilderSmallContainer"
        | "useBuilderLargeContainer",
    locationSettings: LocationSettings
): boolean {
    // The feature setting is for production. The feature flag is for development.
    // Apologies for the confusion.

    if (locationSettings.isDevelopment) {
        return getFeatureFlag(flagOrSetting);
    }

    return getFeatureFlag(flagOrSetting) || Math.random() < getFeatureSettingProbability(flagOrSetting);
}

function isProxyEnabled(locationSettings: LocationSettings, superFunction: string): boolean {
    if (locationSettings.isDevelopment) {
        return getFeatureFlag("useFunctionsProxy");
    }

    const flagOrSetting =
        superFunction === "builderFunctionSmall" || superFunction === "builderFunctionLarge"
            ? "useBuilderFunctionsProxy"
            : "useFunctionsProxy";

    return getFeatureFlag(flagOrSetting) || Math.random() < getFeatureSettingProbability(flagOrSetting);
}

export function urlForCloudFunction(
    locationSettings: LocationSettings,
    functionName: string
): { url: string; originKind: OriginKind; superFunction: string | undefined } {
    const path = functionName.split("/");
    const superFunction = functionDispatchTable[path[0]];
    if (superFunction !== undefined) {
        functionName = `${superFunction}/${functionName}`;
    }

    const fullFunctionURL = `${locationSettings.cloudURLPrefix}/${functionName}`;

    let originKind = OriginKind.Cross;
    let callFunctionURL = fullFunctionURL;

    // For some superfunctions, we may direct traffic to the new http containers instead of Cloud Functions
    let useContainer = false;
    switch (superFunction) {
        case "playerFunctionCritical": {
            useContainer = isContainerEnabled("usePlayerCriticalContainer", locationSettings);
            break;
        }
        case "playerFunctionSmall": {
            useContainer = isContainerEnabled("usePlayerSmallContainer", locationSettings);
            break;
        }
        case "playerFunctionLarge": {
            useContainer = isContainerEnabled("usePlayerLargeContainer", locationSettings);
            break;
        }
        case "builderFunctionSmall": {
            useContainer = isContainerEnabled("useBuilderSmallContainer", locationSettings);
            break;
        }
        case "builderFunctionLarge": {
            useContainer = isContainerEnabled("useBuilderLargeContainer", locationSettings);
            break;
        }
    }
    if (useContainer) {
        originKind = OriginKind.Container;
        callFunctionURL = `${locationSettings.containerURLPrefix}/${functionName}`;
    }

    // On short names and custom domains, we want to avoid the overhead of a CORS request
    // and also reduce RTT for non-US connections (this is especially important for setting up new TLS connections).
    // To facilitate this, we may proxy all functions requests through /api/ to Cloud Functions or http containers
    if (isProxyEnabled(locationSettings, superFunction)) {
        // FIXME: This nasty thing is only because we're in "common"; we shouldn't
        // be hiding this here.
        let hostname = "localhost";
        try {
            hostname = window.location.hostname;
        } catch {
            // Since we're in common, and can possibly run via Node, we might
            // not actually have a window.
        }
        const apiPath = originKind === OriginKind.Container ? "api/container" : "api/function";
        callFunctionURL = `${locationSettings.apiURLPrefix(hostname)}/${apiPath}/${functionName}`;
        originKind = OriginKind.Api;
    }

    return { url: callFunctionURL, originKind, superFunction };
}

export async function callCloudFunction(
    locationSettings: LocationSettings,
    functionName: string,
    body: any,
    headers: { [key: string]: string } = {},
    stringify: boolean = true
): Promise<Response | undefined> {
    if (getStaticAppConfig() !== undefined && ignoreInStaticMode.has(functionName)) {
        return undefined;
    }

    const requestBody = stringify ? JSON.stringify(body) : body;
    const { url, originKind, superFunction } = urlForCloudFunction(locationSettings, functionName);

    const response = await singleFetchNoRetry(
        functionName,
        functionName,
        url,
        headers,
        requestBody,
        originKind,
        superFunction,
        superFunction === "builderFunctionSmall" || superFunction === "builderFunctionLarge"
            ? builderCallHeaderTimeout
            : defaultCallHeaderTimeout
    );
    runHandlers(locationSettings, functionName, response === undefined ? onNetworkError : onNetworkSuccess);
    if (response !== undefined) {
        setCloudRegion(response.headers.get("x-fly-region") ?? undefined);
    }
    return response;
}
